import { FunctionComponent, useEffect, useState } from "react";
import {
  ToastType,
  useToast,
} from "../../../context_providers/toast/toast_ctx";
import LoadingWidget from "../../../ui/components/loading_spinner";
import {
  AddressType,
  CustomerAttachmentModel,
  CustomerBillingAddressModel,
  CustomerSpocModel,
  CustomerType,
  defaultCustomer,
  GSTRegisteredStatus,
  LeadSegment,
  RegisteredofficeAddressModel,
  UserModel,
} from "../../../context_providers/auth/user_model";
import RequiredMark from "../../../ui/new_components/common/required_mark";
import { dataTypes } from "../../../utils/import_export_parser";
import Icon from "@mdi/react";
import {
  mdiAccountCircle,
  mdiChevronLeft,
  mdiCurrencyRupee,
  mdiEmailFast,
  mdiLocationEnter,
  mdiMapMarker,
  mdiTruckDelivery,
} from "@mdi/js";
import { stateData } from "../../../utils/states_data";
import { getMissingAddressFields } from "../../../utils/location_util";
import NewBillingWidget from "./components/BillingAddress";
import CustomerSpocSection from "./components/CustomerSpocSection";
import SpocDeliveryAddresses, {
  AddrModalState,
} from "./components/SpocDeliveryAddresses";
import EmployeeSearchWidget from "../../../ui/new_components/emp_search_widget";
import {
  fetchDelete,
  fetchGet,
  fetchPost,
  fetchPut,
} from "../../../service/api_client";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";
import { urls } from "../../../utils/urls";
import CustomerGroupSearchWidget from "./components/customer_group_widget_search";
import { file_dir, FileType } from "../../../utils/enums";
import { getFileType } from "../../purchase_order/model/purchase_order_model";
import { generate_id } from "../journals/destination_stock_voucher/components/item_row_status";
import {
  getAttachmentSignedURLAPI,
  uploadFiletoS3API,
} from "./components/customer_repo";
import MultiAttachmentSection, {
  attachment_category,
} from "./components/MultiAttachmentSection";
import { Country } from "country-state-city";

type CountryType = {
  isoCode: string;
  name: string;
};

interface CustomerCreateScreenProps {
  edit: boolean;
  data?: UserModel;
}

const CustomerCreateScreen: FunctionComponent<CustomerCreateScreenProps> = (
  props,
) => {
  const isUpdatePage = useRouteMatch(`${urls.customer_master}/update/:id`);
  const { id } = useParams<{ id: string }>();
  const { edit } = props;
  const [editAadhar, setEditAdhar] = useState(true);
  const { showToast } = useToast();
  const [customer, setCustomer] = useState<UserModel>({
    ...defaultCustomer, // Ensures default values exist first
    ...(props.data || {}), // Merges props.data without removing defaults
  });

  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState<CountryType[]>([]);
  const [registeredAddress, setRegisteredAddress] =
    useState<RegisteredofficeAddressModel>();
  const [billingAddOpen, setBillingAddOpen] = useState(false);
  const [selectedSpocId, setSelectedSpocId] = useState<string>("");
  const [addrModal, setAddrModal] = useState<AddrModalState | null>(null);
  let url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/users/customer`;
  // Selected values
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [gstFileList, setGSTFileList] = useState<File[]>([]);
  const [attachmentKey, setAttachmentKey] = useState(Math.random() * 10000);
  const [prevAttachments, setPrevAttachments] = useState<any[]>([]); // Store only previously saved files
  const [gstError, setGstError] = useState("");

  const validateGSTNumber = (gst: string): string => {
    if (!gst) return "GST number is required.";
    if (gst.length !== 15)
      return "GST number must be exactly 15 characters. Example: 27ABCDE1234F1Z5";
    if (
      !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(gst)
    ) {
      return "Invalid GST number format. Example: 27ABCDE1234F1Z5";
    }
    return "";
  };

  const extractPanFromGstin = (gstin: string) => {
    if (!gstin || gstin.length !== 15) return null;
    const pan = gstin.substring(2, 12);
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    return panRegex.test(pan) ? pan : null;
  };

  const verifyGstNumber = async () => {
    const gstNo = (customer?.gst ?? customer?.gst_details?.gst_number ?? "")
      .toString()
      .trim()
      .toUpperCase();
    const validationError = validateGSTNumber(gstNo);
    if (validationError) {
      setGstError(validationError);
      showToast({ type: ToastType.error, text: validationError });
      return;
    }

    setLoading(true);
    try {
      const verifyUrl = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/accounts/party/gst/verify`;
      const response = await fetchPost(verifyUrl, { gstin: gstNo });

      if (response.success) {
        if (response.data?.message === "GSTIN Doesn't Exist") {
          showToast({ type: ToastType.error, text: "GSTIN Doesn't Exist" });
          return;
        }
        if (response.data?.message === "GSTIN Exists") {
          const tempData = response.data;
          const validPan = extractPanFromGstin(tempData.GSTIN);
          const verifiedAddress = tempData.principal_place_split_address ?? {};
          const line1 = [
            verifiedAddress.flat_number,
            verifiedAddress.building_name,
            verifiedAddress.building_number,
          ]
            .filter(Boolean)
            .join(", ");

          setCustomer((prev: any) => ({
            ...prev,
            first_name: tempData.legal_name_of_business ?? prev?.first_name,
            gst: tempData.GSTIN,
            gst_details: {
              ...prev?.gst_details,
              gst_number: tempData.GSTIN,
            },
            registered_office_address: {
              ...prev?.registered_office_address,
              line1,
              line2: verifiedAddress.street ?? "",
              line3: verifiedAddress.location ?? "",
              state: verifiedAddress.state ?? "",
              city: verifiedAddress.city ?? "",
              country: "India",
              pincode:
                verifiedAddress.pincode ??
                prev?.registered_office_address?.pincode,
            },
            additional_customer_details: {
              ...prev?.additional_customer_details,
              pan_no:
                validPan || prev?.additional_customer_details?.pan_no || "",
            },
          }));

          setSelectedCountry("India");
          setGstError("");
          showToast({
            type: ToastType.success,
            text: "GST verified successfully! Details have been auto-filled.",
          });
        }
      } else {
        showToast({
          type: ToastType.error,
          text:
            response.message || response.error || "Failed to fetch GST details",
        });
      }
    } catch (error) {
      showToast({
        type: ToastType.error,
        text: "An error occurred while fetching GST details",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const allCountries = Country.getAllCountries();
    setCountries(allCountries);
    if (
      CustomerType.sole_proprietorship ===
      customer?.additional_customer_details?.customer_type
    ) {
      setEditAdhar(false);
    } else setEditAdhar(true);
  }, [editAadhar, customer?.additional_customer_details?.customer_type]);

  useEffect(() => {
    if (customer?.registered_office_address) {
      setRegisteredAddress(customer?.registered_office_address);
    }
  }, [customer?.registered_office_address]);

  // Keep a contact selected so the Delivery Address section always has a target.
  useEffect(() => {
    const list = customer?.spocs || [];
    if (list.length === 0) {
      if (selectedSpocId) setSelectedSpocId("");
      return;
    }
    if (!list.some((s) => s.id === selectedSpocId)) {
      setSelectedSpocId(list[0].id);
    }
  }, [customer?.spocs, selectedSpocId]);

  useEffect(() => {
    // Initialize form with existing data if editing
    if (edit && props.data) {
      setCustomer(props.data);
    }
  }, [edit, props.data]);

  useEffect(() => {
    if (id && isUpdatePage) {
      getData(id); // Fetch data when editing
    }
  }, [id]); // Run when `id` or `isUpdatePage` changes

  useEffect(() => {
    if (
      id &&
      customer?.gst_details?.attachments &&
      prevAttachments.length === 0
    ) {
      // Set only if prevAttachments is empty (avoiding updates with new files)
      setPrevAttachments(customer.gst_details.attachments);
    }
  }, [id, customer?.gst_details?.attachments]); // Runs only when customer data updates

  const getData = async (id: string) => {
    try {
      setLoading(true);
      let url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/users`;
      const res = await fetchGet(`${url}/${id}`);
      if (res.success) {
        const updatedData: any = { ...res.data, type: res.data.type }; // Update type property
        // Back-compat: seed the first contact card from the legacy single
        // contact + flat delivery addresses when spocs aren't present yet.
        if (
          (!updatedData.spocs || updatedData.spocs.length === 0) &&
          (updatedData.customer_contact_details ||
            (updatedData.delivery_address || []).length)
        ) {
          const c = updatedData.customer_contact_details || {};
          updatedData.spocs = [
            {
              id: `spoc-legacy-${updatedData.uid || "0"}`,
              email: c.email ?? updatedData.email ?? "",
              website: c.website ?? "",
              name_of_contact_person: c.name_of_contact_person ?? "",
              designation_of_contact_person:
                c.designation_of_contact_person ?? "",
              mobile_no_of_contact_person:
                c.mobile_no_of_contact_person ?? updatedData.mobile ?? "",
              email_id_of_contact_person: c.email_id_of_contact_person ?? "",
              name_of_accounts_spoc: c.name_of_accounts_spoc ?? "",
              designation_of_accounts_spoc:
                c.designation_of_accounts_spoc ?? "",
              mobile_no_of_accounts_spoc: c.mobile_no_of_accounts_spoc ?? "",
              email_id_of_accounts_spoc: c.email_id_of_accounts_spoc ?? "",
              delivery_addresses: updatedData.delivery_address || [],
            },
          ];
        }
        setCustomer(updatedData); // Update state with merged data
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error) {
      showToast({ type: ToastType.error, text: "An error occurred" });
    } finally {
      setLoading(false);
    }
  };

  const handleBillingAddressAdded = (
    newAddress: CustomerBillingAddressModel,
  ) => {
    setCustomer((prev) => {
      if (!prev) return prev; // Ensure customer is not undefined

      return {
        ...prev,
        billing_address: [...(prev.billing_address || []), newAddress], // Append new address
      };
    });
  };

  const handleDeliveryAddressAdded = (
    newAddress: CustomerBillingAddressModel,
  ) => {
    setCustomer((prev) => {
      if (!prev) return prev; // Ensure customer is not undefined

      return {
        ...prev,
        delivery_address: [...(prev.delivery_address || []), newAddress], // Append new address
      };
    });
  };

  const handleBillingAddressUpdated = (
    updatedAddress: CustomerBillingAddressModel,
  ) => {
    setCustomer((prev) => {
      if (!prev) return prev;
      const branchKey = (updatedAddress.branch_name ?? "").trim().toLowerCase();
      return {
        ...prev,
        billing_address: (prev.billing_address || []).map((a) =>
          (a.branch_name ?? "").trim().toLowerCase() === branchKey
            ? updatedAddress
            : a,
        ),
      };
    });
  };

  const handleBillingAddressDeleted = (
    deletedAddress: CustomerBillingAddressModel,
  ) => {
    setCustomer((prev) => {
      if (!prev) return prev;
      const branchKey = (deletedAddress.branch_name ?? "").trim().toLowerCase();
      return {
        ...prev,
        billing_address: (prev.billing_address || []).filter(
          (a) => (a.branch_name ?? "").trim().toLowerCase() !== branchKey,
        ),
      };
    });
  };

  const uploadFile = async ({
    file,
    category,
  }: {
    file: File;
    category: string;
  }) => {
    try {
      if (!file || !category) {
        showToast({
          type: ToastType.warning,
          text: "Please select file and category",
        });
        return;
      }
      setLoading(true);
      const { name, type } = file;

      const query = {
        mimetype: type,
        extension: name.split(".").pop(),
        dir: file_dir.customer_attachments,
      };
      const res = await getAttachmentSignedURLAPI(query);
      if (res.success) {
        const { signed_url, url } = res.data;

        await uploadFiletoS3API({
          signed_url,
          file,
          type: getFileType(type),
        });

        const attachment: CustomerAttachmentModel = {
          id: generate_id(),
          category,
          type: getFileType(type),
          url,
          deleted: false,
        };
        showToast({
          type: ToastType.success,
          text: "Upload succesful",
        });

        setCustomer((o: any) => {
          if (!o) return o; // Ensure o is not undefined
          return {
            ...o,
            attachments: [...(o.attachments || []), attachment],
          };
        });

        setAttachmentKey(Math.random() * 1000);
        setLoading(false);
        return attachment;
      }
      setLoading(false);
    } catch (error: any) {
      showToast({
        type: ToastType.error,
        text: error.message,
      });
      setLoading(false);
    }
  };

  const submit = async () => {
    if (
      !customer.additional_customer_details?.sales_person &&
      !customer.additional_customer_details?.relationship_manager
    ) {
      showToast({
        type: ToastType.error,
        text: "Please add sales person and relationship manager.",
      });
      return;
    }
    if (
      customer.additional_customer_details?.customer_type !==
      CustomerType.sole_proprietorship
    ) {
      if (customer.additional_customer_details) {
        customer.additional_customer_details.aadhaar_no = " ";
      }
      if (!customer.additional_customer_details?.aadhaar_no) {
        showToast({
          type: ToastType.error,
          text: "Please add aadhar number.",
        });
        return;
      }
    }
    const missingRegistered = getMissingAddressFields(
      customer?.registered_office_address,
    );
    if (missingRegistered.length) {
      showToast({
        type: ToastType.error,
        text: `Registered Office Address: please fill ${missingRegistered.join(
          ", ",
        )}`,
      });
      return;
    }
    if (!customer?.billing_address?.length) {
      showToast({
        type: ToastType.error,
        text: "Please add at least one billing address",
      });
      return;
    }
    const spocsToValidate = customer?.spocs || [];
    if (spocsToValidate.length === 0) {
      showToast({
        type: ToastType.error,
        text: "Please add at least one SPOC with a delivery address",
      });
      return;
    }
    for (let i = 0; i < spocsToValidate.length; i++) {
      const s = spocsToValidate[i];
      if (
        !s.name_of_contact_person?.trim() ||
        !s.mobile_no_of_contact_person?.trim()
      ) {
        showToast({
          type: ToastType.error,
          text: `SPOC ${i + 1}: name and contact number are required`,
        });
        return;
      }
      if (!s.delivery_addresses || s.delivery_addresses.length === 0) {
        showToast({
          type: ToastType.error,
          text: `SPOC ${i + 1} (${s.name_of_contact_person}): add at least one delivery address`,
        });
        return;
      }
    }
    setLoading(true);
    // const res = await CreateUserRepo(customer!);
    let form = new FormData();
    if (gstFileList && gstFileList.length) {
      gstFileList.forEach((f) => {
        form.append("customerAttachments", f);
      });
    }
    // Back-compat mirror: keep the legacy single contact + flat delivery list in
    // sync with the first SPOC so existing readers / order flow keep working.
    const primarySpoc = (customer?.spocs || [])[0];
    const customerToSave: any = { ...customer };
    if (primarySpoc) {
      customerToSave.customer_contact_details = {
        ...(customer?.customer_contact_details || {}),
        email:
          primarySpoc.email || primarySpoc.email_id_of_contact_person || "",
        website: primarySpoc.website || "",
        name_of_contact_person: primarySpoc.name_of_contact_person || "",
        designation_of_contact_person:
          primarySpoc.designation_of_contact_person || "",
        mobile_no_of_contact_person:
          primarySpoc.mobile_no_of_contact_person || "",
        email_id_of_contact_person:
          primarySpoc.email_id_of_contact_person || "",
        name_of_accounts_spoc: primarySpoc.name_of_accounts_spoc || "",
        designation_of_accounts_spoc:
          primarySpoc.designation_of_accounts_spoc || "",
        mobile_no_of_accounts_spoc:
          primarySpoc.mobile_no_of_accounts_spoc || "",
        email_id_of_accounts_spoc: primarySpoc.email_id_of_accounts_spoc || "",
      };
      customerToSave.mobile =
        primarySpoc.mobile_no_of_contact_person || customer?.mobile || "";
      customerToSave.email =
        primarySpoc.email ||
        primarySpoc.email_id_of_contact_person ||
        customer?.email ||
        "";
      customerToSave.delivery_address = (customer?.spocs || []).flatMap(
        (s: any) => s.delivery_addresses || [],
      );
    }

    if (customer !== undefined)
      form.append("customerData", JSON.stringify(customerToSave));
    const res = customer?.uid
      ? await fetchPut(url + "/" + customer.uid, form)
      : await fetchPost(url, form);
    // const res = await fetchPost(url, customer);
    if (res.success) {
      showToast({ type: ToastType.success, text: res.message });
      setLoading(false);
      // setCustomer(res.data);
      history.push(urls.customer_master);
    } else {
      showToast({ type: ToastType.error, text: res.error });
    }
    setLoading(false);
  };

  const onDeleteFile = (attachment: CustomerAttachmentModel) => {
    setCustomer((prev: any) => {
      if (!prev) return prev;

      const updatedAttachments = prev.attachments.map(
        (att: CustomerAttachmentModel) =>
          att.id === attachment.id ? { ...att, deleted: true } : att,
      );

      return {
        ...prev,
        attachments: updatedAttachments,
      };
    });

    showToast({
      type: ToastType.success,
      text: "Attachment removed",
    });
  };

  const handleDeleteSpoc = async (spoc: CustomerSpocModel) => {
    // Persisted customer (edit mode): delete on the server immediately so its
    // delivery-address rows are soft-deleted and the deleter is captured.
    if (customer?.uid) {
      setLoading(true);
      const res = await fetchDelete(`${url}/${customer.uid}/spoc/${spoc.id}`);
      setLoading(false);
      if (!res.success) {
        showToast({ type: ToastType.error, text: res.error });
        return;
      }
      showToast({ type: ToastType.success, text: res.message });
    }

    const remaining = (customer?.spocs || []).filter((s) => s.id !== spoc.id);
    setCustomer((prev: any) => ({ ...prev, spocs: remaining }));
    if (selectedSpocId === spoc.id) {
      setSelectedSpocId(remaining[0]?.id || "");
    }
  };

  const handleDeleteSpocAddress = async (
    spocId: string,
    addr: CustomerBillingAddressModel,
  ) => {
    if (customer?.uid) {
      setLoading(true);
      const res = await fetchDelete(
        `${url}/${customer.uid}/spoc/${spocId}/address?branch=${encodeURIComponent(
          addr.branch_name || "",
        )}`,
      );
      setLoading(false);
      if (!res.success) {
        showToast({ type: ToastType.error, text: res.error });
        return;
      }
      showToast({ type: ToastType.success, text: res.message });
    }

    setCustomer((prev: any) => ({
      ...prev,
      spocs: (prev?.spocs || []).map((s: any) =>
        s.id === spocId
          ? {
              ...s,
              delivery_addresses: (s.delivery_addresses || []).filter(
                (a: any) =>
                  (a.branch_name || "").trim() !==
                  (addr.branch_name || "").trim(),
              ),
            }
          : s,
      ),
    }));
  };

  return (
    <>
      <div className="flex items-center justify-between p-2 sticky top-0 bg-myBgcolor">
        <div className="flex flex-col md:flex-row">
          <button
            id="customer-create-back-btn"
            className="flex items-center w-fit md:p-2 mt-2 hover:text-gray-700 font-bold text-lg"
            onClick={() => {
              history.goBack();
            }}
          >
            <Icon path={mdiChevronLeft} className="h-6 w-6 font-bold"></Icon>{" "}
            Back
          </button>
          <div
            id="customer-create-heading"
            className="text-xl font-bold border-l-4 border-myPrimaryColor p-2 md:pl-2 mt-2 md:mt-4"
          >
            Customer {id ? `Update: ${id}` : "Create"}
          </div>
        </div>
      </div>
      <form
        id="customer-create-form"
        onSubmit={(e) => {
          e.preventDefault();
          //   if (window.confirm("Are your to create new customer ?")) {
          //     createCustomer();
          //   }
        }}
        className="grid gap-2 p-2 md:p-5 w-full overflow-hidden"
      >
        <section
          id="customer-create-gst-verification-section"
          className="flex flex-col w-full md:w-1/2 md:gap-4 rounded-md bg-white border p-3 shadow-md"
        >
          <h1 className="mb-2 font-semibold text-sm md:text-base flex gap-2 items-center">
            <span className="text-myPrimaryColor">
              <Icon path={mdiCurrencyRupee} size={0.8} />
            </span>
            GST Verification
          </h1>
          <div className="flex flex-col gap-1">
            <label
              htmlFor="customer-create-gst-lookup"
              className="text-sm font-medium text-gray-700"
            >
              GST Number
            </label>
            <div className="flex gap-2">
              <input
                id="customer-create-gst-lookup"
                type="text"
                maxLength={15}
                data-type={dataTypes.string}
                className="border rounded-md px-3 py-2 flex-grow uppercase focus:ring-2 focus:ring-blue-500 outline-none"
                value={customer?.gst || customer?.gst_details?.gst_number || ""}
                onChange={(e) => {
                  const val = e.target.value.toUpperCase();
                  setCustomer((prev: any) => ({
                    ...prev,
                    gst: val,
                    gst_details: {
                      ...prev?.gst_details,
                      gst_number: val,
                    },
                  }));
                  setGstError(validateGSTNumber(val));
                }}
                placeholder="Enter GST to auto-fill details"
              />
              <button
                id="customer-create-gst-verify-btn"
                type="button"
                onClick={verifyGstNumber}
                disabled={
                  !!validateGSTNumber(
                    (
                      customer?.gst ||
                      customer?.gst_details?.gst_number ||
                      ""
                    ).toUpperCase(),
                  )
                }
                className={`px-4 py-2 rounded-md text-white ${
                  validateGSTNumber(
                    (
                      customer?.gst ||
                      customer?.gst_details?.gst_number ||
                      ""
                    ).toUpperCase(),
                  )
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-myPrimaryColor hover:opacity-90"
                }`}
              >
                Search
              </button>
            </div>
            {gstError && (
              <p
                id="customer-create-gst-error"
                className="text-xs text-red-500"
              >
                {gstError}
              </p>
            )}
          </div>
        </section>

        <section
          id="customer-create-details-section"
          className="flex flex-col w-full md:w-1/2 md:gap-4 rounded-md bg-white border p-3 shadow-md"
        >
          <h1 className="mb-2 font-semibold text-sm md:text-base flex gap-2 items-center">
            <span className="text-myPrimaryColor">
              <Icon path={mdiAccountCircle} size={0.8} />
            </span>
            Customer Details
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
            {/* Customer Name */}
            <div className="flex flex-col gap-1">
              <label
                htmlFor="customer-create-name"
                className="text-sm font-medium text-gray-700"
              >
                Customer Name <RequiredMark />
              </label>
              <input
                required
                title="Customer name is required"
                className="border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none w-full"
                type="text"
                data-type={dataTypes.string}
                id="customer-create-name"
                name="name"
                value={customer?.first_name}
                onChange={(e) => {
                  const first_name = e.target.value;
                  setCustomer((prev: any) => ({
                    ...prev,
                    first_name: first_name ? first_name : "",
                  }));
                }}
                placeholder="Enter Customer Name"
              />
            </div>

            {/* Customer Group */}
            <div className="flex flex-col gap-1">
              <label
                htmlFor="customer-create-customer-group"
                className="text-sm font-medium text-gray-700"
              >
                Customer Group <RequiredMark />
              </label>
              {/* <input
                required
                className="border rounded-md px-3 py-2 capitalize focus:ring-2 focus:ring-blue-500 outline-none"
                id="group"
                name="group"
                data-type={dataTypes.string}
                value={customer?.additional_customer_details?.customer_group}
                // onChange={handleChange}
                onChange={(e) => {
                  const customer_group = e.target.value;
                  setCustomer((prev: any) => ({
                    ...prev,
                    additional_customer_details: {
                      ...prev.additional_customer_details!,
                      customer_group: customer_group ? customer_group : "",
                    },
                  }));
                }}
                placeholder="Enter Customer Group"
              /> */}
              <CustomerGroupSearchWidget
                id="customer-create-customer-group"
                placeholder=""
                value={customer?.additional_customer_details?.customer_group}
                onSelect={(val: any) => {
                  setCustomer((prev: any) => ({
                    ...prev,
                    additional_customer_details: {
                      ...((prev && prev.additional_customer_details) || {}), // Ensure it is always an object
                      customer_group: val || "",
                    },
                  }));
                }}
              />
            </div>

            {/* Lead Segment */}
            <div className="flex flex-col gap-1">
              <label
                htmlFor="customer-create-lead-segment"
                className="text-sm font-medium text-gray-700"
              >
                Lead Segment <RequiredMark />
              </label>
              <select
                required
                className="border rounded-md px-3 py-2 capitalize focus:ring-2 focus:ring-blue-500 outline-none"
                id="customer-create-lead-segment"
                name="lead_segment"
                data-type={dataTypes.string}
                value={customer?.additional_customer_details?.lead_segment}
                onChange={(e) => {
                  const lead_segment = e.target.value;
                  setCustomer((prev: any) => ({
                    ...prev,
                    additional_customer_details: {
                      ...prev.additional_customer_details!,
                      lead_segment: lead_segment ? lead_segment : "",
                    },
                  }));
                }}
              >
                <option value="" hidden>
                  Select
                </option>
                {Object.values(LeadSegment).map((c, i) => (
                  <option value={c} key={i}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            {/* Customer Type */}
            <div className="flex flex-col gap-1">
              <label
                htmlFor="customer-create-customer-type"
                className="text-sm font-medium text-gray-700"
              >
                Customer Type <RequiredMark />
              </label>
              <select
                required
                className="border rounded-md px-3 py-2 capitalize focus:ring-2 focus:ring-blue-500 outline-none"
                id="customer-create-customer-type"
                name="type"
                data-type={dataTypes.string}
                value={customer?.additional_customer_details?.customer_type}
                onChange={(e) => {
                  const customer_type = e.target.value;
                  setCustomer((prev: any) => ({
                    ...prev,
                    additional_customer_details: {
                      ...prev?.additional_customer_details!,
                      customer_type: customer_type ? customer_type : "",
                    },
                  }));
                }}
              >
                <option value="" hidden>
                  Select
                </option>
                {Object.values(CustomerType).map((c, i) => (
                  <option value={c} key={i}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            {/* Customer Brand */}
            <div className="flex flex-col gap-1">
              <label
                htmlFor="customer-create-brand"
                className="text-sm font-medium text-gray-700"
              >
                Brand Name
              </label>
              <input
                className="border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none w-full"
                type="text"
                data-type={dataTypes.string}
                id="customer-create-brand"
                name="brand"
                value={customer?.additional_customer_details?.brand}
                onChange={(e) => {
                  const brand = e.target.value;

                  setCustomer((prev: any) => ({
                    ...prev,
                    additional_customer_details: {
                      ...prev?.additional_customer_details!,
                      brand: brand ? brand : "",
                    },
                  }));
                }}
                placeholder="Enter Brand"
              />
            </div>

            {/*Store Name*/}
            <div className="flex flex-col gap-1">
              <label
                htmlFor="customer-create-store-name"
                className="text-sm font-medium text-gray-700"
              >
                Store Name
              </label>
              <input
                required
                title="Store Name is required"
                className="border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none w-full"
                type="text"
                data-type={dataTypes.string}
                id="customer-create-store-name"
                name="store_name"
                value={customer?.additional_customer_details?.store_name}
                onChange={(e) => {
                  const storeName = e.target.value;

                  setCustomer((prev: any) => ({
                    ...prev,
                    additional_customer_details: {
                      ...prev?.additional_customer_details!,
                      store_name: storeName ? storeName : "",
                    },
                  }));
                }}
                placeholder="Enter Store Name"
              />
            </div>
          </div>
        </section>
        <div className="grid grid-cols-1  md:grid-cols-2 lg:grid-cols-2 gap-3">
          {/* Address Start */}
          <section
            id="customer-create-registered-address-section"
            className="py-4 bg-white rounded-md p-3 shadow-md"
          >
            {/* Heading */}
            <h1 className="mb-4 font-semibold text-sm md:text-base flex gap-2 items-center">
              <span className="text-myPrimaryColor">
                <Icon path={mdiLocationEnter} size={0.9} />
              </span>
              Registered Office Address
            </h1>

            {/* Address Form */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="flex flex-col gap-4">
                {/* Address Line 1 */}
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="customer-create-registered-line1"
                    className="text-sm font-medium text-gray-700"
                  >
                    Address <RequiredMark />
                  </label>
                  <input
                    required
                    type="text"
                    data-type={dataTypes.string}
                    className="border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                    id="customer-create-registered-line1"
                    name="line1"
                    value={customer?.registered_office_address?.line1}
                    // onChange={handleChange}
                    onChange={(e) => {
                      const line1 = e.target.value;
                      setCustomer((prev: any) => ({
                        ...prev,
                        registered_office_address: {
                          ...prev?.registered_office_address!,
                          line1: line1 ? line1 : "",
                        },
                      }));
                    }}
                    placeholder="Line 1"
                  />
                </div>

                {/* Address Line 2 */}
                <div className="flex flex-col gap-1">
                  <input
                    type="text"
                    data-type={dataTypes.string}
                    className="border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                    id="customer-create-registered-line2"
                    name="line2"
                    value={customer?.registered_office_address?.line2}
                    onChange={(e) => {
                      const line2 = e.target.value;
                      setCustomer((prev: any) => ({
                        ...prev,
                        registered_office_address: {
                          ...prev.registered_office_address,
                          line2: line2 ? line2 : "",
                        },
                      }));
                    }}
                    placeholder="Line 2"
                  />
                </div>

                {/* Address Line 3 */}
                <div className="flex flex-col gap-1">
                  <input
                    type="text"
                    data-type={dataTypes.string}
                    className="border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                    id="customer-create-registered-line3"
                    name="address.line3"
                    value={customer?.registered_office_address?.line3}
                    onChange={(e) => {
                      const line3 = e.target.value;
                      setCustomer((prev: any) => ({
                        ...prev,
                        registered_office_address: {
                          ...prev.registered_office_address,
                          line3: line3 ? line3 : "",
                        },
                      }));
                    }}
                    placeholder="Line 3"
                  />
                </div>

                {/* Country Selection */}
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="customer-create-registered-country"
                    className="text-sm font-medium text-gray-700"
                  >
                    Country <RequiredMark />
                  </label>
                  <select
                    id="customer-create-registered-country"
                    name="country"
                    className="border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                    value={customer?.registered_office_address?.country || ""}
                    onChange={(event) => {
                      const selectedCountryName = event.target.value;
                      if (selectedCountryName)
                        setSelectedCountry(selectedCountryName);
                      setCustomer((prev: any) => ({
                        ...prev,
                        registered_office_address: {
                          ...prev?.registered_office_address!,
                          country: selectedCountryName,
                        },
                      }));
                    }}
                  >
                    <option value="">Select Country</option>
                    {countries.map((country) => (
                      <option key={country.isoCode} value={country.name}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Right Column */}
              <div className="flex flex-col gap-4">
                {/* State Selection */}
                {selectedCountry === "India" ? (
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor="customer-create-registered-state"
                      className="text-sm font-medium text-gray-700"
                    >
                      State <RequiredMark />
                    </label>
                    <select
                      id="customer-create-registered-state"
                      name="state"
                      className="border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                      value={customer?.registered_office_address?.state}
                      onChange={(newValue) => {
                        const selectedStateValue = (
                          newValue.target as HTMLSelectElement
                        ).value;
                        setCustomer((prev: any) => ({
                          ...prev,
                          registered_office_address: {
                            ...prev.registered_office_address,
                            state: selectedStateValue ? selectedStateValue : "",
                          },
                        }));
                      }}
                    >
                      <option value="">Select State</option>
                      {stateData.map((state) => (
                        <option key={state.id} value={state.name}>
                          {state.name}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor="customer-create-registered-state"
                      className="text-sm font-medium text-gray-700"
                    >
                      State <RequiredMark />
                    </label>
                    <input
                      type="text"
                      data-type={dataTypes.string}
                      className="border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                      id="customer-create-registered-state"
                      name="state1"
                      value={customer?.registered_office_address?.state}
                      onChange={(e) => {
                        const selectedStateValue = e.target.value;
                        setCustomer((prev: any) => ({
                          ...prev,
                          registered_office_address: {
                            ...prev.registered_office_address,
                            state: selectedStateValue ? selectedStateValue : "",
                          },
                        }));
                      }}
                      placeholder="State"
                    />
                  </div>
                )}

                {/* City */}
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="customer-create-registered-city"
                    className="text-sm font-medium text-gray-700"
                  >
                    City <RequiredMark />
                  </label>
                  <input
                    type="text"
                    data-type={dataTypes.string}
                    className="border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                    id="customer-create-registered-city"
                    name="city"
                    value={customer?.registered_office_address?.city}
                    // onChange={handleChange}
                    onChange={(newValue) => {
                      const selectedCityValue = newValue.target.value;
                      setCustomer((prev: any) => ({
                        ...prev,
                        registered_office_address: {
                          ...prev.registered_office_address,
                          city: selectedCityValue ? selectedCityValue : "",
                        },
                      }));
                    }}
                    placeholder="City"
                  />
                </div>

                {/* Postal Code */}
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="customer-create-registered-pincode"
                    className="text-sm font-medium text-gray-700"
                  >
                    Postal Code <RequiredMark />
                  </label>
                  <input
                    required
                    type="text"
                    data-type={dataTypes.number}
                    className="border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                    id="customer-create-registered-pincode"
                    name="pincode"
                    value={customer?.registered_office_address?.pincode}
                    onChange={(e) => {
                      const pincode = Number(e.target.value);
                      setCustomer((prev: any) => ({
                        ...prev,
                        registered_office_address: {
                          ...prev.registered_office_address,
                          pincode: pincode ? pincode : "",
                        },
                      }));
                    }}
                    placeholder="Postal Code"
                  />
                </div>
              </div>
            </div>
          </section>

          <section
            id="customer-create-gst-details-section"
            className="py-4 bg-white rounded-md p-3 shadow-md gap-2"
          >
            <h1 className="mb-2 font-semibold text-sm md:text-base flex gap-2 items-center">
              <span className="text-myPrimaryColor">
                <Icon path={mdiCurrencyRupee} size={0.7} />
              </span>
              GST Details
            </h1>

            {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-2"> */}
            <div className="flex flex-col gap-4 bg-white p-4 ">
              {/* GST Registration */}
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="customer-create-gst-registration"
                  className="text-sm font-medium text-gray-700"
                >
                  GST Registration <RequiredMark />
                </label>
                <select
                  required
                  data-type={dataTypes.string}
                  className="border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                  id="customer-create-gst-registration"
                  name="gst_registration"
                  value={customer?.gst_details?.gst_registration}
                  onChange={(e) => {
                    const gst_registration = e.target.value;
                    setCustomer((prev: any) => ({
                      ...prev,
                      gst_details: {
                        ...prev.gst_details!,
                        gst_registration: gst_registration
                          ? gst_registration
                          : "",
                      },
                    }));
                  }}
                >
                  <option value="" hidden>
                    Select
                  </option>
                  {Object.values(GSTRegisteredStatus).map((c, i) => (
                    <option value={c} key={i}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              {/* GST Number (entered & verified in the GST Verification section above) */}
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="customer-create-gst-number"
                  className="text-sm font-medium text-gray-700"
                >
                  GST Number
                </label>
                <input
                  type="text"
                  data-type={dataTypes.string}
                  disabled
                  className="border rounded-md px-3 py-2 bg-gray-100 outline-none"
                  id="customer-create-gst-number"
                  name="gst_no_display"
                  value={
                    customer?.gst || customer?.gst_details?.gst_number || ""
                  }
                  placeholder="Enter GST in the GST Verification section above"
                />
              </div>

              {/* Attachments */}

              <div className="flex flex-col gap-1">
                <label
                  htmlFor="customer-create-gst-attachments"
                  className="text-sm font-medium text-gray-700"
                >
                  Attach Files
                </label>

                <input
                  id="customer-create-gst-attachments"
                  type="file"
                  multiple
                  className="border px-2 py-1 rounded-md"
                  onChange={(e) => {
                    if (e.target.files) {
                      const newFiles = Array.from(e.target.files);
                      setGSTFileList((prevFiles) => {
                        const updatedFiles = [...prevFiles, ...newFiles];

                        setCustomer((prevCustomer: any) => {
                          if (!prevCustomer) return prevCustomer;

                          return {
                            ...prevCustomer,
                            gst_details: {
                              ...prevCustomer.gst_details!,
                              attachments: [
                                ...(prevAttachments || []), // Keep previous attachments
                                ...updatedFiles.map((file) => ({
                                  type: file.type.includes("image")
                                    ? "Image"
                                    : file.type.includes("pdf")
                                      ? "PDF"
                                      : "Other",
                                  category: "GST",
                                  name: file.name,
                                  url: URL.createObjectURL(file), // Temporary URL for preview
                                })),
                              ],
                            },
                          };
                        });

                        return updatedFiles;
                      });
                    }
                  }}
                />

                {/* Display newly uploaded files */}
                <div className="w-full mt-2 mx-auto">
                  {gstFileList.length > 0 &&
                    gstFileList.map((file: File, index: number) => (
                      <ul
                        key={index}
                        id={`customer-create-gst-file-${index}`}
                        className="py-2 m-2 flex justify-between rounded-md text-black border-2 px-4"
                      >
                        <li key={index} className="flex items-center gap-2">
                          {file ? (
                            // Check if the file has a URL (for image/PDF preview)
                            file.type === FileType.IMAGE ? (
                              <img
                                src={file.name}
                                alt={file.name}
                                className="w-16 h-16 object-cover"
                              />
                            ) : file.type === FileType.PDF ? (
                              <embed
                                src={file.name}
                                type="application/pdf"
                                className="w-16 h-16"
                              />
                            ) : (
                              <span>{file.name}</span> // For other file types, display file name
                            )
                          ) : (
                            // If file doesn't have a URL, just display file name
                            <span>{"No file selected"}</span>
                          )}
                        </li>
                        <button
                          id={`customer-create-gst-file-remove-btn-${index}`}
                          type="button"
                          className="text-red-500"
                          onClick={() => {
                            setGSTFileList((prevFiles) =>
                              prevFiles.filter(
                                (_, currIndex) => currIndex !== index,
                              ),
                            );
                          }}
                        >
                          Remove
                        </button>
                      </ul>
                    ))}
                </div>

                {/* Display only previously saved files */}
                {id &&
                  prevAttachments.map((attachment: any, index: number) => (
                    <ul
                      key={attachment.id || index}
                      id={`customer-create-gst-saved-file-${index}`}
                      className="py-2 m-2 flex justify-between items-center rounded-md text-black border-2 px-4"
                    >
                      <li className="flex items-center gap-2">
                        {attachment.type === "PDF" ? (
                          <a
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500"
                          >
                            {attachment.category} (PDF)
                          </a>
                        ) : attachment.type === "Image" ? (
                          <a
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500"
                          >
                            {attachment.category} (IMAGE)
                          </a>
                        ) : (
                          <span>{attachment.name || "Unknown File"}</span>
                        )}
                      </li>
                      <button
                        id={`customer-create-gst-saved-file-remove-btn-${index}`}
                        type="button"
                        className="text-red-500"
                        onClick={() => {
                          setPrevAttachments((prev) =>
                            prev.filter((_, currIndex) => currIndex !== index),
                          );
                          setCustomer((prevCustomer: any) => {
                            if (!prevCustomer) return prevCustomer;
                            return {
                              ...prevCustomer,
                              gst_details: {
                                ...prevCustomer.gst_details!,
                                attachments:
                                  prevCustomer.gst_details.attachments.filter(
                                    (_: any, currIndex: any) =>
                                      currIndex !== index,
                                  ),
                              },
                            };
                          });
                        }}
                      >
                        Remove
                      </button>
                    </ul>
                  ))}
              </div>
            </div>

            {/* </div> */}
          </section>
        </div>
        <>
          <section
            id="customer-create-billing-address-section"
            className="py-4 bg-white rounded-md p-3 shadow-md"
          >
            <h1 className="mb-2 font-semibold text-sm md:text-base flex gap-2 items-center justify-between">
              <span className="flex gap-2 items-center">
                <span className="text-myPrimaryColor">
                  <Icon path={mdiMapMarker} size={0.8} />
                </span>
                Billing Address
                <span className="text-base text-red-500">*</span>
              </span>
              {!customer?.billing_address?.length && (
                <button
                  id="customer-create-billing-add-new-btn-mobile"
                  type="button"
                  onClick={() => setBillingAddOpen(true)}
                  className="md:hidden text-xs text-white bg-myPrimaryColor px-3 py-1 rounded font-semibold"
                >
                  Add new
                </button>
              )}
            </h1>
            <NewBillingWidget
              id_prefix="customer-create-billing"
              uid={customer?.uid}
              type={AddressType.billing}
              registered_address={registeredAddress}
              onBillingAddressAdded={handleBillingAddressAdded}
              onBillingAddressUpdated={handleBillingAddressUpdated}
              onBillingAddressDeleted={handleBillingAddressDeleted}
              setRegisteredAddress={setRegisteredAddress}
              addOpen={billingAddOpen}
              onAddOpenChange={setBillingAddOpen}
            />
          </section>
          <section
            id="customer-create-contact-details-section"
            className="py-4 bg-white rounded-md p-3 shadow-md"
          >
            <h1 className="mb-2 font-semibold text-sm md:text-base flex gap-2 items-center">
              <span className="text-myPrimaryColor">
                <Icon path={mdiEmailFast} size={0.8} />
              </span>
              Contact Details
            </h1>
            <CustomerSpocSection
              id_prefix="customer-create-spoc"
              spocs={customer?.spocs || []}
              onChange={(spocs) =>
                setCustomer((prev: any) => ({ ...prev, spocs }))
              }
              selectedSpocId={selectedSpocId}
              onSelectSpoc={setSelectedSpocId}
              onAddAddress={(id) => {
                setSelectedSpocId(id);
                setAddrModal({ spocId: id, addrIndex: null });
              }}
              onDeleteSpoc={handleDeleteSpoc}
            />
            <div
              style={{ display: "none" }}
              className="grid grid-cols-1 md:grid-cols-3 gap-3"
            >
              <div className="grid gap-1">
                <label htmlFor="contact_mobile" className="text-sm">
                  Contact No <RequiredMark />
                </label>

                <input
                  type="tel"
                  pattern="[1-9]{1}[0-9]{9}"
                  className="border rounded-md px-3 py-1"
                  id="contact_mobile"
                  name="contact_mobile"
                  value={customer?.mobile ?? ""}
                  onChange={(e) => {
                    const mobile = e.target.value;
                    setCustomer((prev: any) => ({
                      ...prev,
                      mobile: mobile ? mobile : "",
                    }));
                  }}
                />
              </div>
              <div className="grid gap-1">
                <label htmlFor="contact_name" className="text-sm">
                  Name of Contact Person <RequiredMark />
                </label>
                <input
                  required
                  type="string"
                  data-type={dataTypes.string}
                  className="border rounded-md px-3 py-1"
                  id="contact_name"
                  name="contact_name"
                  value={
                    customer?.customer_contact_details?.name_of_contact_person
                  }
                  // onChange={handleChange}
                  onChange={(e) => {
                    const name_of_contact_person = e.target.value;
                    setCustomer((prev: any) => ({
                      ...prev,
                      customer_contact_details: {
                        ...prev.customer_contact_details,
                        name_of_contact_person: name_of_contact_person
                          ? name_of_contact_person
                          : "",
                      },
                    }));
                  }}
                />
              </div>
              <div className="grid gap-1">
                <label
                  htmlFor="contact_email_contact_person"
                  className="text-sm"
                >
                  Email ID of Contact Person
                </label>
                <input
                  data-type={dataTypes.string}
                  type="email"
                  className="border rounded-md px-3 py-1"
                  id="contact_email_contact_person"
                  name="contact_email_contact_person"
                  value={
                    customer?.customer_contact_details
                      ?.email_id_of_contact_person
                  }
                  // onChange={handleChange}
                  onChange={(e) => {
                    const email_id_of_contact_person = e.target.value;
                    setCustomer((prev: any) => ({
                      ...prev,
                      email: email_id_of_contact_person
                        ? email_id_of_contact_person
                        : "",
                      customer_contact_details: {
                        ...prev.customer_contact_details,
                        email_id_of_contact_person: email_id_of_contact_person
                          ? email_id_of_contact_person
                          : "",
                      },
                    }));
                  }}
                />
              </div>
              <div className="grid gap-1">
                <label htmlFor="email_id_of_accounts_spoc" className="text-sm">
                  Email ID of Account SPOC
                </label>
                <input
                  data-type={dataTypes.string}
                  type="email"
                  className="border rounded-md px-3 py-1"
                  id="email_id_of_accounts_spoc"
                  name="email_id_of_accounts_spoc"
                  value={
                    customer?.customer_contact_details
                      ?.email_id_of_accounts_spoc
                  }
                  // onChange={handleChange}
                  onChange={(e) => {
                    const email_id_of_accounts_spoc = e.target.value;
                    setCustomer((prev: any) => ({
                      ...prev,
                      customer_contact_details: {
                        ...prev.customer_contact_details,
                        email_id_of_accounts_spoc: email_id_of_accounts_spoc
                          ? email_id_of_accounts_spoc
                          : "",
                      },
                    }));
                  }}
                />
              </div>
              <div className="grid gap-1">
                <label htmlFor="contact_email" className="text-sm">
                  Email ID <RequiredMark />
                </label>
                <input
                  required
                  data-type={dataTypes.string}
                  type="email"
                  className="border rounded-md px-3 py-1"
                  id="email"
                  name="email"
                  value={customer?.customer_contact_details?.email}
                  // onChange={handleChange}
                  onChange={(e) => {
                    const email = e.target.value;
                    setCustomer((prev: any) => ({
                      ...prev,
                      customer_contact_details: {
                        ...prev.customer_contact_details,
                        email: email ? email : "",
                      },
                    }));
                  }}
                />
              </div>
              <div className="grid gap-1">
                <label htmlFor="designation" className="text-sm">
                  Designation
                </label>
                <input
                  data-type={dataTypes.string}
                  type="text"
                  className="border rounded-md px-3 py-1"
                  id="designation"
                  name="designation"
                  value={
                    customer?.customer_contact_details
                      ?.designation_of_contact_person
                  }
                  // onChange={handleChange}
                  onChange={(e) => {
                    const designation_of_contact_person = e.target.value;
                    setCustomer((prev: any) => ({
                      ...prev,
                      customer_contact_details: {
                        ...prev.customer_contact_details,
                        designation_of_contact_person:
                          designation_of_contact_person
                            ? designation_of_contact_person
                            : "",
                      },
                    }));
                  }}
                />
              </div>
              <div className="grid gap-1">
                <label htmlFor="name_ceo_director" className="text-sm">
                  Name of Account SPOC
                </label>
                <input
                  data-type={dataTypes.string}
                  type="text"
                  className="border rounded-md px-3 py-1"
                  id="name_of_accounts_spoc"
                  name="name_of_accounts_spoc"
                  value={
                    customer?.customer_contact_details?.name_of_accounts_spoc
                  }
                  // onChange={handleChange}
                  onChange={(e) => {
                    const name_of_accounts_spoc = e.target.value;
                    setCustomer((prev: any) => ({
                      ...prev,
                      customer_contact_details: {
                        ...prev.customer_contact_details,
                        name_of_accounts_spoc: name_of_accounts_spoc
                          ? name_of_accounts_spoc
                          : "",
                      },
                    }));
                  }}
                />
              </div>
              <div className="grid gap-1">
                <label htmlFor="name_account_spoc" className="text-sm">
                  Designation of Accounts SPOC
                </label>
                <input
                  data-type={dataTypes.string}
                  type="text"
                  className="border rounded-md px-3 py-1"
                  id="designation_of_accounts_spoc"
                  name="designation_of_accounts_spoc"
                  value={
                    customer?.customer_contact_details
                      ?.designation_of_accounts_spoc
                  }
                  // onChange={handleChange}
                  onChange={(e) => {
                    const designation_of_accounts_spoc = e.target.value;
                    setCustomer((prev: any) => ({
                      ...prev,
                      customer_contact_details: {
                        ...prev.customer_contact_details,
                        designation_of_accounts_spoc:
                          designation_of_accounts_spoc
                            ? designation_of_accounts_spoc
                            : "",
                      },
                    }));
                  }}
                />
              </div>
              <div className="grid gap-1">
                <label htmlFor="website" className="text-sm">
                  Website
                </label>
                <input
                  data-type={dataTypes.string}
                  type="text"
                  className="border rounded-md px-3 py-1"
                  id="website"
                  name="website"
                  value={customer?.customer_contact_details?.website}
                  // onChange={handleChange}
                  onChange={(e) => {
                    const website = e.target.value;
                    setCustomer((prev: any) => ({
                      ...prev,
                      customer_contact_details: {
                        ...prev.customer_contact_details,
                        website: website ? website : "",
                      },
                    }));
                  }}
                />
              </div>
              <div className="grid gap-1">
                <label
                  htmlFor="contact_mobile_contact_person"
                  className="text-sm"
                >
                  Mobile No. of contact person
                </label>
                <input
                  type="tel"
                  pattern="[1-9]{1}[0-9]{9}"
                  data-type={dataTypes.string}
                  className="border rounded-md px-3 py-1"
                  id="contact_mobile_contact_person"
                  name="contact_mobile_contact_person"
                  value={
                    customer?.customer_contact_details
                      ?.mobile_no_of_contact_person
                  }
                  // onChange={handleChange}
                  onChange={(e) => {
                    const mobile_no_of_contact_person = Number(e.target.value);
                    setCustomer((prev: any) => ({
                      ...prev,
                      customer_contact_details: {
                        ...prev.customer_contact_details,
                        mobile_no_of_contact_person: mobile_no_of_contact_person
                          ? mobile_no_of_contact_person
                          : "",
                      },
                    }));
                  }}
                />
              </div>

              <div className="grid gap-1">
                <label
                  htmlFor="contact_mobile_accounts_spoc"
                  className="text-sm"
                >
                  Mobile No. of Accounts SPOC
                </label>
                <input
                  type="number"
                  data-type={dataTypes.string}
                  className="border rounded-md px-3 py-1"
                  id="contact_mobile_accounts_spoc"
                  name="contact_mobile_accounts_spoc"
                  value={
                    customer?.customer_contact_details
                      ?.mobile_no_of_accounts_spoc
                  }
                  // onChange={handleChange}
                  onChange={(e) => {
                    const mobile_no_of_accounts_spoc = Number(e.target.value);
                    setCustomer((prev: any) => ({
                      ...prev,
                      customer_contact_details: {
                        ...prev.customer_contact_details,
                        mobile_no_of_accounts_spoc: mobile_no_of_accounts_spoc
                          ? mobile_no_of_accounts_spoc
                          : "",
                      },
                    }));
                  }}
                />
              </div>
            </div>
          </section>
          <section
            id="customer-create-delivery-address-section"
            className="py-4 bg-white rounded-md p-3 shadow-md"
          >
            <h1 className="mb-2 font-semibold text-sm md:text-base flex gap-2 items-center">
              <span className="text-myPrimaryColor">
                <Icon path={mdiTruckDelivery} size={0.8} />
              </span>
              Delivery Address
              <span className="text-base text-red-500">*</span>
            </h1>
            <SpocDeliveryAddresses
              id_prefix="customer-create-delivery"
              spocs={customer?.spocs || []}
              onChange={(spocs) =>
                setCustomer((prev: any) => ({ ...prev, spocs }))
              }
              selectedSpocId={selectedSpocId}
              addrModal={addrModal}
              onAddrModalChange={setAddrModal}
              onDeleteAddress={handleDeleteSpocAddress}
            />
          </section>
        </>
        <section id="customer-create-additional-details-section" className="py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3 bg-white rounded-md p-2">
            <div className="grid gap-1">
              <label htmlFor="customer-create-pan-no" className="text-sm">
                PAN No{" "}
                {!!(customer?.gst || customer?.gst_details?.gst_number) && (
                  <RequiredMark />
                )}
              </label>
              <input
                required={
                  !!(customer?.gst || customer?.gst_details?.gst_number)
                }
                type="text"
                data-type={dataTypes.string}
                className="border rounded-md px-3 py-1"
                id="customer-create-pan-no"
                name="pan_no"
                value={customer?.additional_customer_details?.pan_no}
                onChange={(e) => {
                  const pan_no = e.target.value;
                  setCustomer((prev: any) => ({
                    ...prev,
                    additional_customer_details: {
                      ...prev.additional_customer_details!,
                      pan_no: pan_no ? pan_no : "",
                    },
                  }));
                }}
              />
            </div>
            {customer.additional_customer_details?.customer_type ===
              CustomerType.sole_proprietorship && (
              <div className="grid gap-1">
                <label htmlFor="customer-create-aadhaar-no" className="text-sm">
                  Aadhar No <RequiredMark />
                </label>
                <input
                  required
                  disabled={editAadhar}
                  type="text"
                  data-type={dataTypes.string}
                  className="border rounded-md px-3 py-1"
                  id="customer-create-aadhaar-no"
                  name="aadhar_no"
                  value={customer?.additional_customer_details?.aadhaar_no}
                  onChange={(e) => {
                    const aadhaar_no = e.target.value;
                    setCustomer((prev: any) => ({
                      ...prev,
                      additional_customer_details: {
                        ...prev.additional_customer_details!,
                        aadhaar_no: aadhaar_no ? aadhaar_no : "",
                      },
                    }));
                  }}
                />
              </div>
            )}
            <div className="grid gap-1">
              <label htmlFor="customer-create-dealer-code" className="text-sm">
                Dealer Code
              </label>
              <input
                data-type={dataTypes.string}
                type="text"
                className="border rounded-md px-3 py-1"
                id="customer-create-dealer-code"
                name="dealer_code"
                value={customer?.additional_customer_details?.dealer_code}
                // onChange={handleChange}
                onChange={(e) => {
                  const dealer_code = e.target.value;
                  setCustomer((prev: any) => ({
                    ...prev,
                    additional_customer_details: {
                      ...prev.additional_customer_details!,
                      dealer_code: dealer_code ? dealer_code : "",
                    },
                  }));
                }}
              />
            </div>
            <div className="grid gap-1">
              <label htmlFor="customer-create-sales-person" className="text-sm">
                Sales person *
              </label>
              <EmployeeSearchWidget
                id="customer-create-sales-person"
                withBtn={false}
                onSelect={(emp) => {
                  setCustomer((pv) => {
                    return {
                      ...pv!,
                      additional_customer_details: {
                        ...pv?.additional_customer_details,
                        sales_person_uid: emp.uid,
                        sales_person:
                          emp.first_name + " " + (emp.last_name ?? ""),
                      },
                    };
                  });
                }}
                emp={
                  customer?.additional_customer_details?.sales_person_uid &&
                  customer?.additional_customer_details?.sales_person
                    ? {
                        uid: customer.additional_customer_details
                          ?.sales_person_uid!,
                        name: customer.additional_customer_details.sales_person,
                      }
                    : undefined
                }
              />
            </div>
            <div className="grid gap-1">
              <label
                htmlFor="customer-create-relationship-manager"
                className="text-sm"
              >
                Relationship Manager *
              </label>
              <EmployeeSearchWidget
                id="customer-create-relationship-manager"
                withBtn={false}
                onSelect={(emp) => {
                  setCustomer((pv) => {
                    return {
                      ...pv!,
                      additional_customer_details: {
                        ...pv?.additional_customer_details,
                        rm_uid: emp.uid,
                        relationship_manager:
                          emp.first_name + " " + (emp.last_name ?? ""),
                      },
                    };
                  });
                }}
                emp={
                  customer?.additional_customer_details?.rm_uid &&
                  customer?.additional_customer_details?.relationship_manager
                    ? {
                        uid: customer.additional_customer_details?.rm_uid!,
                        name: customer.additional_customer_details
                          .relationship_manager,
                      }
                    : undefined
                }
              />
            </div>
          </div>
        </section>
        <div id="customer-create-attachments-section">
          <MultiAttachmentSection
            id_prefix="customer-create-attachment"
            attachments={customer?.attachments?.filter(
              (attachment) =>
                attachment.category === attachment_category.other &&
                attachment.deleted === false,
            )}
            onSubmit={async function (data: { file: File; category: string }) {
              const attachment = await uploadFile({
                file: data.file,
                category: data.category,
              });
              if (!attachment) {
                showToast({
                  type: ToastType.error,
                  text: "Upload failed",
                });
                throw new Error("File upload failed");
              }
              return attachment;
            }}
            category={attachment_category.other}
            onDelete={function (data: CustomerAttachmentModel) {
              onDeleteFile(data);
            }}
          />
        </div>

        <div className="flex justify-end gap-5 mt-3">
          {!id && (
            <button
              id="customer-create-submit-btn"
              onClick={() => {
                submit();
              }}
              className="cursor-pointer text-white bg-myPrimaryColor px-3 py-1 rounded font-semibold"
            >
              Create
            </button>
          )}
          {id && (
            <button
              id="customer-create-update-btn"
              onClick={() => {
                submit();
              }}
              className="cursor-pointer text-white bg-myPrimaryColor px-3 py-1 rounded font-semibold"
            >
              Update
            </button>
          )}
          <button
            id="customer-create-quit-btn"
            onClick={() => {
              history.push(urls.customer_master);
            }}
            className="rounded-md border border-green-600 text-sm font-semibold bg-white text-black px-3 py-1"
          >
            Quit
          </button>
        </div>
      </form>{" "}
      <LoadingWidget loading={loading} />
    </>
  );
};

export default CustomerCreateScreen;
